import {fullHeight} from "@models/helpers.ts";
import type {PermissaoAplicacao} from "@models/seguranca/permissaoApp.ts";
import {Button, type ButtonOwnProps, SxProps, Tooltip} from "@mui/material";
import React, {PropsWithChildren, useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";

export interface PermissionableButtonProps {
    permission?: PermissaoAplicacao[],
    color?: ButtonOwnProps['color']
    onClick?: () => void
    icon?: React.ReactNode
    label?: string
    fullWidth?: boolean
    fullHeight?: boolean
    goTo?: string | {
        fullUrl: string
        permissao?: readonly PermissaoAplicacao[] | PermissaoAplicacao[]
    }
    query?: object,
    showEvenWithoutAccess?: true | undefined
    disabled?: boolean,
    tooltip?: string,
    loading?: boolean,
    sx?: SxProps
}

export function PermissionableButton(props: PropsWithChildren<PermissionableButtonProps>) {
    const {hasAccess, permissoes} = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [reqPerm, setReqPerm] = useState<PermissaoAplicacao[]>([]);
    const {
        showEvenWithoutAccess: _1,
        permission: _2,
        goTo: _3,
        query: _4,
        tooltip: _5,
        loading,
        fullHeight: _fheight,
        ...buttonProps
    } = props;


    useEffect(() => {
        if (props.goTo) {
            if (typeof props.goTo === 'string') {
                if (props.permission != null)
                    setReqPerm(props.permission);
            } else {
                if (props.goTo.permissao && props.permission != null && props.permission?.length > 0) {
                    setReqPerm(props.permission.concat(props.goTo.permissao as unknown as PermissaoAplicacao[]));
                }
            }
        } else {
            if (props.permission)
                setReqPerm(props.permission);
        }

    }, [props.permission, props.goTo]);
    const permissionDenied = useMemo(() => {
        return (reqPerm?.length > 0) && !hasAccess(reqPerm ?? [])
    }, [reqPerm, permissoes])

    const btSx = props.fullHeight ? {...fullHeight, ...props.sx} : {...props.sx};

    const shouldShow = useMemo(() => {
        if (!permissionDenied)
            return true

        return (permissionDenied && props.showEvenWithoutAccess);
    }, [props.permission, props.showEvenWithoutAccess, permissionDenied])

    return (shouldShow && <Tooltip
        title={props.tooltip ?? (permissionDenied ? "Você não tem permissão para acessar essa funcionalidade" : "")}>
        <Button endIcon={props.icon}
                size={'small'}
                loading={loading}
                sx={{
                    ...btSx
                }}
                {...buttonProps}
                onClick={() => {
                    if (props.goTo) {
                        if (typeof props.goTo === 'string') {
                            navigate({
                                pathname: props.goTo,
                                search: searchParams.toString(),
                            });
                        } else {
                            if (props.goTo.fullUrl) {

                                navigate({
                                    pathname: props.goTo.fullUrl,
                                    search: searchParams.toString(),
                                });
                            }
                        }
                    }
                    if (props.onClick) {
                        props.onClick();
                    }
                }}
                variant="outlined"
                disabled={props.disabled || permissionDenied}
                fullWidth={props.fullWidth ?? true}>
            {props.children && props.children || props.label && props.label}
        </Button>
    </Tooltip>);
}